import { EventBus } from '../utils/eventBus';
import { ImageUploadService } from '../services/imageUpload';
import { ApiService } from '../services/api';
import { ImageGalleryModal } from './ImageGalleryModal';
import { createElement, createButton, clearElement } from '../utils/dom';

export class InputArea {
  private container: HTMLElement | null = null;
  private textarea: HTMLTextAreaElement | null = null;
  private imagePreviewContainer: HTMLElement | null = null;
  private imageInput: HTMLInputElement | null = null;
  private selectedImages: Map<string, { file?: File; url: string; previewUrl: string }> = new Map();
  private imageGalleryModal: ImageGalleryModal;
  private imageSelectedHandler!: (url: string) => void;
  private uploadRequestedHandler!: () => void;
  private galleryRequestedHandler!: () => void;
  private viewportResizeHandler: (() => void) | null = null;

  constructor(
    private eventBus: EventBus,
    private imageUploadService: ImageUploadService,
    private apiService: ApiService
  ) {
    this.imageGalleryModal = new ImageGalleryModal(eventBus, apiService);
    this.imageSelectedHandler = (url: string) => this.addSelectedImage(url);
    this.uploadRequestedHandler = () => this.triggerImageUpload();
    this.galleryRequestedHandler = () => this.imageGalleryModal.show();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventBus.on('image:selected', this.imageSelectedHandler);
    this.eventBus.on('toolbar:uploadImage', this.uploadRequestedHandler);
    this.eventBus.on('toolbar:openGallery', this.galleryRequestedHandler);
  }

  mount(container: HTMLElement): void {
    this.container = container;
    this.render();
    this.setupViewportHandler();
  }

  private setupViewportHandler(): void {
    if (!this.isMobileDevice()) return;

    // Use visualViewport API to handle virtual keyboard
    if (window.visualViewport) {
      this.viewportResizeHandler = () => {
        this.handleViewportResize();
      };
      window.visualViewport.addEventListener('resize', this.viewportResizeHandler);
      window.visualViewport.addEventListener('scroll', this.viewportResizeHandler);
    }
  }

  private handleViewportResize(): void {
    if (!this.container || !window.visualViewport) return;

    const viewport = window.visualViewport;
    const windowHeight = window.innerHeight;
    const viewportHeight = viewport.height;

    // Calculate keyboard height
    const keyboardHeight = windowHeight - viewportHeight;

    if (keyboardHeight > 100) {
      // Keyboard is open - scroll input into view
      requestAnimationFrame(() => {
        this.textarea?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }
  }

  private handleFocus(): void {
    if (!this.isMobileDevice()) return;

    // Delay to allow keyboard to fully open
    setTimeout(() => {
      this.textarea?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 300);
  }

  private render(): void {
    if (!this.container) return;

    clearElement(this.container);

    // Image preview area
    this.imagePreviewContainer = createElement('div', 'image-preview-container');
    this.container.appendChild(this.imagePreviewContainer);

    // Input row
    const inputRow = createElement('div', 'input-row');

    // Textarea
    this.textarea = document.createElement('textarea');
    this.textarea.className = 'input-textarea';
    this.textarea.placeholder = this.isMobileDevice()
      ? 'Type your message...'
      : 'Type your message... (Enter to send, Shift+Enter for new line)';
    this.textarea.rows = 3;
    this.textarea.setAttribute('enterkeyhint', 'send');
    this.textarea.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.textarea.addEventListener('input', this.autoResize.bind(this));
    this.textarea.addEventListener('focus', this.handleFocus.bind(this));
    inputRow.appendChild(this.textarea);

    // Button group
    const buttonGroup = createElement('div', 'input-button-group');

    // Image upload button
    const imageInput = document.createElement('input');
    imageInput.type = 'file';
    imageInput.accept = 'image/jpeg,image/png,image/gif,image/webp';
    imageInput.style.display = 'none';
    imageInput.addEventListener('change', this.handleImageSelect.bind(this));
    this.imageInput = imageInput;
    buttonGroup.appendChild(imageInput);

    // Send button
    const sendButton = createButton('Send', 'btn btn-primary', this.handleSend.bind(this));
    sendButton.classList.add('input-send-button');
    buttonGroup.appendChild(sendButton);

    inputRow.appendChild(buttonGroup);
    this.container.appendChild(inputRow);
  }

  private autoResize(): void {
    if (!this.textarea) return;
    this.textarea.style.height = 'auto';
    this.textarea.style.height = this.textarea.scrollHeight + 'px';
  }

  private handleKeyDown(e: KeyboardEvent): void {
    // Skip if using IME (e.g., Chinese/Japanese input)
    if (e.isComposing) return;

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  }

  private async handleImageSelect(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (!this.imageUploadService.validateImage(file)) {
      alert('Invalid image file. Please select a JPEG, PNG, GIF, or WebP file under 10MB.');
      return;
    }

    const id = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const previewUrl = await this.imageUploadService.getImagePreviewUrl(file);

    // We'll upload the image when sending the message
    this.selectedImages.set(id, { file, url: '', previewUrl });
    this.renderImagePreviews();

    // Reset input
    input.value = '';
  }

  private renderImagePreviews(): void {
    if (!this.imagePreviewContainer) return;

    clearElement(this.imagePreviewContainer);

    // If no images selected, just clear and return
    if (this.selectedImages.size === 0) return;

    this.selectedImages.forEach((image, id) => {
      const preview = createElement('div', 'image-preview');

      const img = document.createElement('img');
      img.src = image.previewUrl;
      img.alt = 'Preview';
      preview.appendChild(img);

      const removeButton = createButton('✕', 'btn-remove-image', () => {
        this.selectedImages.delete(id);
        this.renderImagePreviews();
      });
      preview.appendChild(removeButton);

      this.imagePreviewContainer!.appendChild(preview);
    });
  }

  private async handleSend(): Promise<void> {
    if (!this.textarea) return;

    const text = this.textarea.value.trim();
    if (!text && this.selectedImages.size === 0) return;

    // Disable input during send
    this.textarea.disabled = true;

    try {
      // Upload any images that haven't been uploaded yet
      const imageUrls: string[] = [];

      for (const [id, image] of this.selectedImages.entries()) {
        if (image.file) {
          // Need to upload this image
          const url = await this.imageUploadService.uploadImage(image.file);
          imageUrls.push(url);
        } else if (image.url) {
          // Already uploaded image from gallery
          imageUrls.push(image.url);
        }
      }

      this.eventBus.emit('message:send', {
        text,
        imageUrls,
      });

      // Clear input
      this.textarea.value = '';
      this.selectedImages.clear();
      this.renderImagePreviews();
      this.textarea.disabled = false;
      this.textarea.focus();
      this.autoResize();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to upload images. Please try again.');
      this.textarea.disabled = false;
    }
  }

  private addSelectedImage(url: string): void {
    const id = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.selectedImages.set(id, { url, previewUrl: url });
    this.renderImagePreviews();
  }

  private triggerImageUpload(): void {
    this.imageInput?.click();
  }

  private isMobileDevice(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(max-width: 768px)').matches;
  }

  destroy(): void {
    this.eventBus.off('image:selected', this.imageSelectedHandler);
    this.eventBus.off('toolbar:uploadImage', this.uploadRequestedHandler);
    this.eventBus.off('toolbar:openGallery', this.galleryRequestedHandler);

    // Remove viewport handlers
    if (this.viewportResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
      window.visualViewport.removeEventListener('scroll', this.viewportResizeHandler);
    }
  }
}
